import React, { useState, useEffect } from 'react';
import {
  Button,
  List,
  ListItem,
  ListItemButton,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import Dialog from '../dialogs/Dialog';
import { getUserGameLists, addGameToList, GameListSummary } from '../../services/contents_api';

interface AddToListButtonProps {
  gameId: number;
  disabled?: boolean;
}

const AddToListButton: React.FC<AddToListButtonProps> = ({ gameId, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [lists, setLists] = useState<GameListSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [addingToList, setAddingToList] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchLists = async (page: number = 1, append: boolean = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const response = await getUserGameLists(undefined, page);
      if (append) {
        setLists(prev => [...prev, ...response.results]);
      } else {
        setLists(response.results);
      }
      setTotalCount(response.count);
      setCurrentPage(page);
    } catch (err) {
      setError('リストの取得に失敗しました');
      console.error('リスト取得エラー:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchLists();
    }
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
    setSuccess(null);
    setError(null);
  };

  const handleClose = () => {
    setOpen(false);
    setSuccess(null);
    setError(null);
  };

  const handleAddToList = async (listId: number) => {
    setAddingToList(listId);
    setError(null);
    setSuccess(null);
    try {
      await addGameToList(listId, gameId);
      const list = lists.find(l => l.id === listId);
      setSuccess(`「${list?.title}」に追加しました`);
      // リストを更新
      await fetchLists();
    } catch (err: any) {
      if (err.response?.status === 400) {
        setError('このゲームは既にリストに追加されています');
      } else {
        setError('リストへの追加に失敗しました');
      }
      console.error('リスト追加エラー:', err);
    } finally {
      setAddingToList(null);
    }
  };

  const handleLoadMore = () => {
    fetchLists(currentPage + 1, true);
  };

  const hasMore = lists.length < totalCount;

  const dialogContent = (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : error && lists.length === 0 ? (
        <Alert severity="error">{error}</Alert>
      ) : lists.length === 0 ? (
        <Box sx={{ textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2, fontSize: '0.8rem' }}>
            リストがまだありません
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            href="/profile?tab=lists"
            onClick={handleClose}
            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
          >
            新しいリストを作成
          </Button>
        </Box>
      ) : (
        <>
          {success && (
            <Box sx={{ mb: 2, mx: -3, px: 3 }}>
              <Alert
                severity="success"
                onClose={() => setSuccess(null)}
                sx={{
                  fontSize: '0.75rem',
                  wordBreak: 'break-word',
                  width: '100%',
                  maxWidth: '90%',
                  boxSizing: 'border-box',
                  overflowWrap: 'break-word'
                }}
              >
                {success}
              </Alert>
            </Box>
          )}
          {error && (
            <Box sx={{ mb: 2, mx: -3, px: 3 }}>
              <Alert
                severity="error"
                onClose={() => setError(null)}
                sx={{
                  fontSize: '0.75rem',
                  wordBreak: 'break-word',
                  width: '100%',
                  maxWidth: '90%',
                  boxSizing: 'border-box',
                  overflowWrap: 'break-word'
                }}
              >
                {error}
              </Alert>
            </Box>
          )}
          <List sx={{ py: 0, mx: -3 }}>
            {lists.map((list) => (
              <ListItem key={list.id} disablePadding>
                <ListItemButton
                  onClick={() => handleAddToList(list.id)}
                  disabled={addingToList !== null}
                  sx={{
                    py: 1.5,
                    px: 3,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.08)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'center',
                      width: '100%'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        minWidth: 60,
                        height: 60,
                        position: 'relative'
                      }}
                    >
                      {list.preview_items.slice(0, 2).map((item, index) => (
                        <Box
                          key={item.id}
                          component="img"
                          src={item.game_cover || '/image/no_image.png'}
                          alt=""
                          sx={{
                            width: index === 0 ? 32 : 24,
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 0.5,
                            backgroundColor: 'rgba(0,0,0,0.2)'
                          }}
                        />
                      ))}
                      {list.preview_items.length === 0 && (
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: 0.5,
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <PlaylistAddIcon sx={{ opacity: 0.3 }} />
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.8rem'
                        }}
                      >
                        {list.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.7rem' }}
                      >
                        {list.item_count}本のゲーム
                      </Typography>
                    </Box>

                    {addingToList === list.id && (
                      <CircularProgress size={20} />
                    )}
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <Button
                variant="text"
                size="small"
                onClick={handleLoadMore}
                disabled={loadingMore}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary'
                  }
                }}
              >
                {loadingMore ? <CircularProgress size={16} /> : 'さらに読み込む'}
              </Button>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pt: hasMore ? 1 : 2, borderTop: hasMore ? 'none' : '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              href="/profile?tab=lists"
              onClick={handleClose}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem'
              }}
            >
              新しいリストを作成
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleOpen}
        disabled={disabled}
        sx={{
          textTransform: 'none',
          fontSize: { xs: '0.7rem', sm: '0.75rem' },
          fontWeight: 400,
          padding: { xs: '6px 12px', sm: '8px 12px' },
          minWidth: 'auto',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          lineHeight: '1',
          borderRadius: '12px',
          color: 'text.primary',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            transform: 'translateY(-1px)',
          }
        }}
      >
        <PlaylistAddIcon fontSize="small" sx={{ color: 'text.primary' }} />
        <Box sx={{ display: { xs: 'none', sm: 'block' }, ml: 0.5 }}>リストに追加</Box>
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        title="リストに追加"
        content={dialogContent}
        showCloseButton={true}
        width={500}
      />
    </>
  );
};

export default AddToListButton;
